import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { Seo } from '@/components/Seo';
import { Reveal } from '@/components/public/anim';
import { useLivePageContent } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { User, ArrowRight } from 'lucide-react';

const SLUG = 'chi-sono';

// Contenuti di DEFAULT (fallback): usati finché l'admin non inserisce override dal CRM.
const DEFAULTS = {
  eyebrow: 'Sila Experiences',
  title: 'Chi sono',
  lead: 'Riscopri la tua natura attraverso la Natura della Sila.',
  hero: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1920&q=80',
  authorName: 'Carmen Fiore',
  authorRole: "Creatrice dell'App Sila Experiences",
  authorImage: '',
  intro: [
    "Ciao, sono Carmen Fiore, creatrice dell'App Sila Experiences.",
    "Questa pagina è in fase di completamento: presto troverai qui la mia storia e il percorso che ha dato vita al progetto Sila Experiences.",
  ].join('\n\n'),
  ctaLabel: 'Scopri il Progetto',
  ctaTo: '/il-progetto',
};

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

export default function ChiSono() {
  const cms = useLivePageContent(SLUG);

  const c = cms || {};
  const heroImage = str(c.heroImage) || DEFAULTS.hero;
  const eyebrow = str(c.eyebrow) || DEFAULTS.eyebrow;
  const title = str(c.title) || DEFAULTS.title;
  const lead = str(c.lead) || DEFAULTS.lead;
  const authorName = str(c.authorName) || DEFAULTS.authorName;
  const authorRole = str(c.authorRole) || DEFAULTS.authorRole;
  const authorImage = str(c.authorImage) || DEFAULTS.authorImage;
  const intro = (typeof c.intro === 'string' && c.intro.trim()) ? c.intro : DEFAULTS.intro;
  const paragraphs = intro.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const ctaLabel = str(c.ctaLabel) || DEFAULTS.ctaLabel;
  const ctaTo = str(c.ctaTo) || DEFAULTS.ctaTo;

  return (
    <PublicLayout>
      <Seo title={title} description={lead} path="/chi-sono" image={heroImage} />

      <SilaHero
        image={heroImage}
        badge={eyebrow}
        title={title}
        subtitle={lead}
        icon={User}
        testId="chi-sono-hero"
      />

      <section className="sila-section py-16 md:py-20" data-testid="chi-sono-body">
        <div className="grid gap-10 lg:grid-cols-[320px_1fr] lg:gap-12">
          {/* Foto — placeholder in attesa dell'immagine reale */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <Card className="overflow-hidden">
              {authorImage ? (
                <img src={authorImage} alt={authorName} className="aspect-[4/5] w-full object-cover" data-testid="chi-sono-foto" />
              ) : (
                <div className="flex aspect-[4/5] w-full items-center justify-center bg-muted" data-testid="chi-sono-foto-placeholder">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <User className="h-14 w-14" />
                    <span className="text-xs font-medium">Foto in arrivo</span>
                  </div>
                </div>
              )}
              <div className="p-5">
                <p className="font-display text-lg font-semibold">{authorName}</p>
                <p className="mt-1 text-sm text-muted-foreground">{authorRole}</p>
              </div>
            </Card>
          </aside>

          {/* Testo introduttivo (editabile dal CRM, default dal PDF) */}
          <div className="space-y-5 text-[17px] leading-relaxed text-foreground/90" data-testid="chi-sono-text">
            {paragraphs.map((p, i) => (
              <Reveal key={i}>
                {i === 0
                  ? <p className="font-display text-2xl font-semibold text-foreground">{p}</p>
                  : <p>{p}</p>}
              </Reveal>
            ))}
            {ctaLabel && (
              <Reveal>
                <div className="pt-2">
                  <Button asChild variant="outline" data-testid="chi-sono-progetto-cta">
                    <Link to={ctaTo}>{ctaLabel} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
